import { useEventCallback } from "@src/appV2/lib/utils/useEventCallback";
import { useFlowStack } from "@src/appV2/lib/utils/useFlowStack";
import { useCallback, useState } from "react";

export type ShiftCancelFlowStep =
  | "content"
  | "restriction-warning"
  | "confirmed"
  | "education"
  | "restricted";

interface UseShiftCancelFlowConfig<TConfirmedData extends { isBecomingRestricted: boolean }> {
  isBecomingRestricted: boolean;
  onExecuteCancellation: () => Promise<TConfirmedData>;
  onFetchRestrictionData: () => Promise<{ liftTime?: string }>;
  onExit: () => void;
  onKeepShift: () => void;
}

interface UseShiftCancelFlowResult<TConfirmedData> {
  currentStep: ShiftCancelFlowStep;
  canGoBack: boolean;
  confirmedData: TConfirmedData | undefined;
  isCancelling: boolean;
  isLoadingRestrictionData: boolean;
  liftTime: string | undefined;
  handlePressAndHoldToCancel: () => Promise<void>;
  handleRestrictionWarningCancel: () => Promise<void>;
  handleKeepShift: () => void;
  handleWhyCantIUseCredit: () => void;
  handleEducationBack: () => void;
  handleConfirmationContinue: () => Promise<void>;
  handleRestrictedContinue: () => void;
  reset: () => void;
}

export function useShiftCancelFlow<TConfirmedData extends { isBecomingRestricted: boolean }>(
  config: UseShiftCancelFlowConfig<TConfirmedData>
): UseShiftCancelFlowResult<TConfirmedData> {
  const flow = useFlowStack<ShiftCancelFlowStep>("content");

  const [confirmedData, setConfirmedData] = useState<TConfirmedData | undefined>(undefined);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isLoadingRestrictionData, setIsLoadingRestrictionData] = useState(false);
  const [liftTime, setLiftTime] = useState<string | undefined>(undefined);

  const executeCancellation = useEventCallback(async () => {
    setIsCancelling(true);
    try {
      const data = await config.onExecuteCancellation();
      setConfirmedData(data);
      flow.replace("confirmed");
    } finally {
      setIsCancelling(false);
    }
  });

  const handlePressAndHoldToCancel = useEventCallback(async () => {
    if (config.isBecomingRestricted) {
      flow.push("restriction-warning");
    } else {
      await executeCancellation();
    }
  });

  const handleRestrictionWarningCancel = useEventCallback(async () => {
    await executeCancellation();
  });

  const handleKeepShift = useEventCallback(() => {
    config.onKeepShift();
  });

  const handleWhyCantIUseCredit = useEventCallback(() => {
    flow.push("education");
  });

  const handleEducationBack = useEventCallback(() => {
    flow.pop();
  });

  const handleConfirmationContinue = useEventCallback(async () => {
    if (confirmedData?.isBecomingRestricted) {
      setIsLoadingRestrictionData(true);
      try {
        const data = await config.onFetchRestrictionData();
        setLiftTime(data.liftTime);
      } catch {
        // Graceful degradation: show restricted screen without liftTime
      } finally {
        setIsLoadingRestrictionData(false);
      }
      flow.push("restricted");
    } else {
      config.onExit();
    }
  });

  const handleRestrictedContinue = useEventCallback(() => {
    config.onExit();
  });

  const reset = useCallback(() => {
    flow.reset();
    setConfirmedData(undefined);
    setIsCancelling(false);
    setIsLoadingRestrictionData(false);
    setLiftTime(undefined);
  }, [flow]);

  return {
    currentStep: flow.currentStep,
    canGoBack: flow.canGoBack,
    confirmedData,
    isCancelling,
    isLoadingRestrictionData,
    liftTime,
    handlePressAndHoldToCancel,
    handleRestrictionWarningCancel,
    handleKeepShift,
    handleWhyCantIUseCredit,
    handleEducationBack,
    handleConfirmationContinue,
    handleRestrictedContinue,
    reset,
  };
}
